import React, { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getBlackOutDatesRequest } from "../../../../../redux/users/slice";
import Loading from "../../../../../components/loading/loading";
import { updateBlackOutDateRequest } from "../../../../../utils/APICalls";
import ErrorNotification from "../../../../../components/error-notification/error-notification";

type Availability = "available" | "unavailable";

type Props = {
  data: any;
};

const BlackOutCalendar = (props: Props) => {
  const { data } = props;
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [availability, setAvailability] = useState<
    Record<string, Availability>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();
  const {
    loading,
    user: { blackOutDates },
  } = useSelector((state: any) => state.user);

  const getApiParams = () => {
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");

    return {
      id: data?.id,
      fromDate: startOfMonth.format("YYYY-MM-DD"),
      toDate: endOfMonth.format("YYYY-MM-DD"),
    };
  };

  useEffect(() => {
    if (data?.id && !isLoading) {
      dispatch(getBlackOutDatesRequest(getApiParams()));
    }
    // eslint-disable-next-line
  }, [data, dispatch, currentDate]);

  useEffect(() => {
    if (blackOutDates && !isLoading) {
      const availabilityState: Record<string, Availability> = {};
      blackOutDates.forEach((blackOutDate: any) => {
        availabilityState[blackOutDate] = "unavailable";
      });
      setAvailability(availabilityState);
    }
    // eslint-disable-next-line
  }, [blackOutDates, isLoading]);

  const toggleDateAvailability = async (date: Dayjs) => {
    const dateString = date.format("YYYY-MM-DD");
    const isCurrentlyBlocked = availability[dateString] === "unavailable";

    setIsLoading(true);
    const response = await updateBlackOutDateRequest(data?.id, {
      date: dateString,
      available: isCurrentlyBlocked,
    });
    setIsLoading(false);
    if (response?.status === 204) {
      setAvailability((prev) => ({
        ...prev,
        [dateString]: isCurrentlyBlocked ? "available" : "unavailable",
      }));
      dispatch(getBlackOutDatesRequest(getApiParams()));
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const toggle = async (d: Dayjs) => {
    const today = dayjs().startOf("day");
    const isViewedMonth = d.isSame(currentDate, "month");
    const isTodayOrFuture = d.isSame(today) || d.isAfter(today);

    if (!isViewedMonth || !isTodayOrFuture) {
      return;
    }

    await toggleDateAvailability(d);
  };

  const isDateDisabled = (d: Dayjs) => {
    const today = dayjs().startOf("day");
    const isTodayOrFuture = d.isSame(today) || d.isAfter(today);

    return !isTodayOrFuture;
  };

  const RectDay = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const key = day.format("YYYY-MM-DD");
    const status = availability[key] ?? "available";
    const isDisabled = isDateDisabled(day);
    const isViewedMonth = day.isSame(currentDate, "month");
    const isClickable =
      !isDisabled && isViewedMonth && status !== "unavailable";

    const backgroundColor = status === "available" ? "#2E7D3214" : "#D32F2F14";

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        onClick={() => toggle(day)}
        disableMargin
        key={`${key}-${status}`}
        sx={{
          margin: 0,
          padding: 0,
          borderRadius: 0,
          width: "100%",
          height: "90px",
          border: "1px solid #c4c4c4",
          borderRight: "none",
          borderBottom: "none",
          color: "inherit",
          cursor: isClickable ? "pointer" : "default",
          transition: "all 0.2s ease",
          backgroundColor: backgroundColor,
          opacity: isDisabled || outsideCurrentMonth ? 0.5 : 1,
          "&:hover": {
            backgroundColor: backgroundColor,
          },
          "&.Mui-selected": {
            backgroundColor: `${backgroundColor} !important`,
            color: "inherit !important",
          },
          "&.Mui-selected:hover": {
            backgroundColor: `${backgroundColor} !important`,
          },
          "&.Mui-selected.Mui-focusVisible": {
            backgroundColor: `${backgroundColor} !important`,
          },
          "&.MuiPickersDay-today": {
            backgroundColor: backgroundColor,
            color: "inherit",
            border: "1px solid #c4c4c4",
            borderRight: "none",
            borderBottom: "none",
          },
          "&.MuiPickersDay-today:hover": {
            backgroundColor: backgroundColor,
          },
          "&.MuiPickersDay-today.Mui-selected": {
            backgroundColor: backgroundColor,
            color: "inherit",
            border: "1px solid #c4c4c4",
            borderRight: "none",
            borderBottom: "none",
          },
          // Override any Material-UI selected states with higher specificity
          "&[data-mui-selected='true']": {
            backgroundColor: `${backgroundColor} !important`,
            color: "inherit !important",
          },
          "&[data-mui-selected='true']:hover": {
            backgroundColor: `${backgroundColor} !important`,
          },
          "&[data-mui-selected='true'].Mui-focusVisible": {
            backgroundColor: `${backgroundColor} !important`,
          },
          "&:nth-of-type(7n)": {
            borderRight: "1px solid #c4c4c4",
          },
          "&:nth-of-type(n + 35), &:nth-of-type(n + 36), &:nth-of-type(n + 37), &:nth-of-type(n + 38), &:nth-of-type(n + 39), &:nth-of-type(n + 40), &:nth-of-type(n + 41), &:nth-of-type(n + 42)":
            {
              borderBottom: "1px solid #c4c4c4",
            },
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          maxWidth: "100%",
          margin: "0 auto",
          height: "100%",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            mt: 2,
          }}
        >
          Click on a day to toggle your availability.
        </Typography>
        <DateCalendar
          value={currentDate}
          onChange={(newDate) => {
            if (newDate) {
              setCurrentDate(newDate);
            }
          }}
          onMonthChange={(newMonth) => {
            setCurrentDate(newMonth);
          }}
          showDaysOutsideCurrentMonth
          dayOfWeekFormatter={(day) => day.toString().charAt(0)}
          slots={{ day: RectDay }}
          slotProps={{ day: { disableMargin: true } as any }}
          disablePast
          sx={{
            width: "100%",
            height: "600px",
            "& [class*='MuiPickersCalendarHeader-labelContainer']": {
              marginRight: "0px !important",
            },
            "& [class*='MuiPickersCalendarHeader-root'] [class*='MuiPickersCalendarHeader-labelContainer']":
              {
                marginRight: "0px !important",
                paddingLeft: "0px !important",
              },
            "& .MuiPickersCalendarHeader-root .MuiPickersCalendarHeader-labelContainer":
              {
                marginRight: "0px !important",
              },
            "& [class*='MuiDayCalendar-header']": {
              justifyContent: "space-around !important",
            },
            "& .MuiDayCalendar-header": {
              justifyContent: "space-around !important",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "24px",
            marginTop: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                width: "24px",
                height: "20px",
                borderRadius: "4px",
                border: "1px solid #c4c4c4",
                backgroundColor: "#2e7d3214",
              }}
            />
            <Typography variant="body2">Available</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                width: "24px",
                height: "20px",
                borderRadius: "4px",
                border: "1px solid #c4c4c4",
                backgroundColor: "#d32f2f14",
              }}
            />
            <Typography variant="body2">Unavailable</Typography>
          </Box>
        </Box>
        {loading || (isLoading && <Loading />)}
        {showErrorNotification && (
          <ErrorNotification
            show={showErrorNotification}
            message={errorMessage}
            onClose={() => setShowErrorNotification(false)}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default BlackOutCalendar;
